import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { ChevronRight, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ProductModal } from "@/components/ProductModal";

export const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await axios.get(`${API}/featured-products`);
        setFeaturedProducts(res.data);
      } catch (err) {
        console.error("Error fetching featured products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallButton(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }
    setInstallPrompt(null);
  };

  const handleProductClick = (product) => {
    // Convert featured product to full product format for modal
    const fullProduct = {
      ...product,
      description: `Premium ${product.category.toLowerCase()} product from our curated collection.`,
      stock: 25, // Default stock for featured
    };
    setSelectedProduct(fullProduct);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://proximart.ro/wp-content/uploads/2025/12/app-banner.jpg"
            alt="Proximart"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
        </div>
        <div className="relative h-full flex flex-col justify-end p-6 md:p-12 lg:p-24">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4 max-w-xl">
            Bine ai venit la Proximart
          </h1>
          <p className="text-white/90 text-base mb-6 max-w-md">
            Magazinul robotizat deschis non stop, mai aproape decat crezi
          </p>
          <button
            data-testid="explore-collection-btn"
            onClick={() => navigate("/locations")}
            className="btn-primary px-8 py-3 rounded-full font-semibold text-sm w-fit flex items-center gap-2"
          >
            Descopera locatiile
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Install App Banner */}
      {showInstallButton && (
        <section className="px-6 md:px-12 lg:px-24 py-6">
          <div className="bg-gradient-to-r from-[#1a4d2e] to-[#2d6a4f] rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Download className="w-5 h-5 text-white" />
              </div>
              <p className="text-white font-medium text-sm">Instalează aplicația Proximart</p>
            </div>
            <button
              onClick={handleInstallClick}
              className="bg-white text-[#1a4d2e] px-4 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors"
            >
              Instalează
            </button>
          </div>
        </section>
      )}

      {/* Concept CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-12">
        <div
          className="relative overflow-hidden rounded-2xl bg-[#1a4d2e] p-8 md:p-12 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => navigate("https://proximart.ro/conceptul/")}
          data-testid="locations-cta"
        >
          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Un magazin cum, probabil, nu ai mai vazut
            </h2>
            <p className="text-white/80 mb-6 max-w-md">
              Descopera conceptul Proximart
            </p>
            <button className="bg-white text-[#1a4d2e] px-6 py-3 rounded-full font-semibold text-sm flex items-center gap-2 hover:bg-gray-100 transition-colors">
              Afla mai multe
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="absolute right-0 bottom-0 opacity-10">
            <svg width="300" height="200" viewBox="0 0 300 200">
              <circle cx="250" cy="150" r="150" fill="white" />
            </svg>
          </div>
        </div>
      </section>
    </div>
  );
};
